class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/**
 * ---------------------------------------------------------------------------
 * Premium cart drawer additions below. Everything above this line is Dawn's
 * original CartDrawer/CartDrawerItems -- unmodified, since add/update/remove
 * all depend on it. Everything below is new: success banner, milestone
 * celebration, countdown, recommendations, and a handful of small delight
 * touches, all built to re-run every time the drawer's markup gets replaced
 * (which happens on every add and every quantity change -- see
 * snippets/cart-drawer.liquid's header comment).
 * ---------------------------------------------------------------------------
 */

(() => {
  const STORAGE_COUNTDOWN_START = 'cartDrawer:countdownStart';
  const SESSION_MILESTONES_REACHED = 'cartDrawer:milestonesReached';

  let lastAddWasGenuineAdd = false;
  let countdownInterval = null;
  let observer = null;
  const drawerRoot = document.getElementById('CartDrawer');

  // The observer watches #CartDrawer for changes so init() re-runs after
  // Dawn's own AJAX cart updates (add, quantity change, cross-tab sync).
  // init() also makes its OWN DOM writes (upsell rendering, success banner,
  // etc) inside that same subtree -- without pausing the observer around
  // those writes, each one would re-trigger init(), which writes again,
  // forever. This was the cause of the upsell section flickering
  // continuously and the jank fighting the open animation.
  function pauseObserver() {
    if (observer) observer.disconnect();
  }

  function resumeObserver() {
    if (observer && drawerRoot) {
      observer.observe(drawerRoot, { childList: true, subtree: true });
    }
  }

  // product-form.js publishes cartUpdate with source:'product-form' for a
  // real add; cart-items.js publishes it with source:'cart-items' for a
  // quantity change. Only the former should show the success banner / sound.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      lastAddWasGenuineAdd = event.source === 'product-form';
    });
  }

  function init() {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    // Captured before initSuccessBanner consumes (resets) the outer flag
    // below -- initScrollToUpsellHint needs the same "was this a genuine
    // add" signal, just later in the sequence (after initUpsell has had a
    // chance to reveal the section it's about to scroll toward).
    const wasGenuineAdd = lastAddWasGenuineAdd;

    pauseObserver();
    try {
      initProgressBarFill(drawer);
      initSuccessBanner(drawer);
      initMilestoneCelebration(drawer);
      initCountdown(drawer);
      initUpsell(drawer);
      initScrollToUpsellHint(drawer, wasGenuineAdd);
      initEmptyShake(drawer);
      initCartBubblePulse();
    } finally {
      resumeObserver();
    }

    // Async and deliberately outside the pause/resume block above: this may
    // itself replace the drawer's HTML (adding/removing the gift line), which
    // is expected to re-trigger the MutationObserver and run init() again --
    // that second pass is what confirms the reconciled state and does nothing
    // further, exactly like initMilestoneCelebration's snapshot approach.
    initGiftAutoAdd();
  }

  // ---- Progress bar fill ------------------------------------------------

  // The fill's correct width is ALWAYS the server-rendered inline
  // --cart-progress-percent var -- nothing below ever writes style.width,
  // so a JS failure can only ever cost the animation, never correctness.
  // (Historical note: the bar once appeared permanently empty; the cause
  // was Dawn's `div:empty { display:none }` in base.css hiding the fill,
  // not any of this animation code. cart-drawer.css carries the fix.)
  //
  // The sweep uses the Web Animations API with explicit from/to keyframes
  // layered over the static width, so the underlying value is untouched.
  // Continuity ("the bar continues from where it was, instead of restarting
  // at zero on every add") requires knowing the pre-update %: Dawn destroys
  // and rebuilds the drawer's HTML on every cart change, so the only
  // reliable moment to read the old value is right before that replacement.
  // Both replacement paths (CartDrawer.renderContents for adds,
  // CartItems.updateQuantity for quantity changes) call
  // getSectionInnerHTML() synchronously just before replacing -- wrapping
  // it captures the value at exactly the right instant.
  // Keyed by each bar's data-cart-progress value ("gift" / "shipping") --
  // there are now two independent bars, not one, so a single previous-%
  // value isn't enough to give each its own correct sweep.
  let pendingPreviousPercents = null;

  const reducedMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Both replacement paths call getSectionInnerHTML TWICE per update (drawer
  // content first, then the cart-icon-bubble section) -- by the second call
  // the fill is already replaced, so only the first call may capture.
  // The consumer resets to null, re-arming for the next cycle.
  function captureFillPercent() {
    if (pendingPreviousPercents !== null) return;
    const captured = {};
    document.querySelectorAll('[data-cart-progress]').forEach((bar) => {
      const key = bar.dataset.cartProgress;
      const fill = bar.querySelector('[data-cart-progress-fill]');
      if (!fill) {
        captured[key] = 0;
        return;
      }
      const parsed = parseFloat(getComputedStyle(fill).getPropertyValue('--cart-progress-percent'));
      captured[key] = Number.isNaN(parsed) ? 0 : parsed;
    });
    pendingPreviousPercents = captured;
  }

  function wrapGetSectionInnerHTML(ClassRef) {
    if (!ClassRef || !ClassRef.prototype || typeof ClassRef.prototype.getSectionInnerHTML !== 'function') return;
    const original = ClassRef.prototype.getSectionInnerHTML;
    ClassRef.prototype.getSectionInnerHTML = function (...args) {
      captureFillPercent();
      return original.apply(this, args);
    };
  }

  wrapGetSectionInnerHTML(typeof CartDrawer !== 'undefined' ? CartDrawer : null);
  wrapGetSectionInnerHTML(typeof CartItems !== 'undefined' ? CartItems : null);

  function sweepFill(fill, fromPercent, toPercent) {
    if (reducedMotion || typeof fill.animate !== 'function' || fromPercent === toPercent) return;

    fill.animate(
      [{ width: `${fromPercent}%` }, { width: `${toPercent}%` }],
      { duration: 700, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'none' }
    );

    // When this sweep arrives at "complete", CSS hides the mover badge
    // (it has landed on top of the Free Shipping badge). But that rule
    // applies from first paint of the re-rendered element, which would
    // hide the badge for the whole ride -- so keep it visible via a WAAPI
    // opacity animation that fades it out just as the sweep lands, then
    // hands off to the CSS opacity:0 resting state.
    const progress = fill.closest('[data-cart-progress]');
    const mover = fill.querySelector('.cart-progress__badge--mover');
    if (mover && progress && progress.classList.contains('is-complete')) {
      mover.animate(
        [
          { opacity: 1, offset: 0 },
          { opacity: 1, offset: 0.85 },
          { opacity: 0, offset: 1 },
        ],
        { duration: 950, easing: 'linear', fill: 'none' }
      );
    }
  }

  // Runs on every init(); only acts when a pre-replacement value was
  // captured -- so countdown ticks and other incidental mutations that
  // re-trigger init() never re-animate an unchanged bar.
  function initProgressBarFill(drawer) {
    if (pendingPreviousPercents === null) return;
    const previous = pendingPreviousPercents;
    pendingPreviousPercents = null;

    drawer.querySelectorAll('[data-cart-progress]').forEach((bar) => {
      const key = bar.dataset.cartProgress;
      const fill = bar.querySelector('[data-cart-progress-fill]');
      if (!fill) return;
      const target = parseFloat(fill.dataset.targetPercent || '0');
      // A bar that didn't exist before this update (e.g. just enabled) has no
      // prior value to sweep from -- render it at rest instead of animating.
      const from = key in previous ? previous[key] : target;
      sweepFill(fill, from, target);
    });
  }

  // ---- Success banner --------------------------------------------------

  function initSuccessBanner(drawer) {
    const banner = drawer.querySelector('[data-cart-drawer-success]');
    if (!banner || !lastAddWasGenuineAdd) return;

    banner.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => banner.classList.add('is-visible')));

    if (document.querySelector('[data-cart-drawer-root]')?.dataset.sound === 'true') {
      playSuccessSound();
    }

    setTimeout(() => {
      banner.classList.remove('is-visible');
      setTimeout(() => {
        banner.hidden = true;
      }, 350);
    }, 2500);

    lastAddWasGenuineAdd = false; // consume the flag so a later re-render (e.g. quantity change) doesn't re-show it
  }

  function playSuccessSound() {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContextClass();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.26);
    } catch (e) {
      // Web Audio can be blocked (autoplay policy, unsupported browser) -- silently skip, never fatal.
    }
  }

  // ---- Milestone celebration ------------------------------------------

  // Fires every time a milestone is genuinely CROSSED (below -> reached),
  // not once per browser session. sessionStorage holds a snapshot of which
  // milestones were reached as of the previous check -- overwritten, never
  // accumulated -- so emptying the cart below the threshold re-arms the
  // celebration, and crossing it again celebrates again. (The earlier
  // accumulate-forever version meant anyone who ever crossed the threshold
  // once never saw the celebration again for the rest of their session,
  // which read as "the celebration doesn't work".)
  // One shared bar now tracks BOTH thresholds (gift, shipping), so a single
  // large add can cross both at once -- detected here by comparing each
  // threshold's current .dataset flag against its own sessionStorage entry,
  // and choosing single vs combined celebration text accordingly.
  function initMilestoneCelebration(drawer) {
    const root = document.querySelector('[data-cart-drawer-root]');
    if (!root || root.dataset.celebration === 'false') return;

    const bar = drawer.querySelector('[data-cart-progress]');
    if (!bar) return;

    const giftReachedNow = bar.dataset.giftReached === 'true';
    const shipReachedNow = bar.dataset.shipReached === 'true';

    let previousGift = false;
    let previousShip = false;
    try {
      previousGift = sessionStorage.getItem(SESSION_MILESTONES_REACHED + ':gift') === 'true';
      previousShip = sessionStorage.getItem(SESSION_MILESTONES_REACHED + ':shipping') === 'true';
    } catch (e) {
      /* storage unavailable -- celebration just fires more often than ideal */
    }

    try {
      sessionStorage.setItem(SESSION_MILESTONES_REACHED + ':gift', String(giftReachedNow));
      sessionStorage.setItem(SESSION_MILESTONES_REACHED + ':shipping', String(shipReachedNow));
    } catch (e) {
      /* ignore */
    }

    const giftNewlyReached = giftReachedNow && !previousGift;
    const shipNewlyReached = shipReachedNow && !previousShip;
    if (!giftNewlyReached && !shipNewlyReached) return;

    let message;
    if (giftNewlyReached && shipNewlyReached) {
      message = "Congratulations! You've unlocked a Free Gift and Free Shipping!";
    } else if (giftNewlyReached) {
      message = 'Congratulations! Your Free Gift has been unlocked.';
    } else {
      message = "Congratulations! You've unlocked Free Shipping!";
    }

    const celebration = bar.querySelector('[data-cart-progress-celebration]');
    if (celebration) {
      const textEl = celebration.querySelector('[data-cart-progress-celebration-text]');
      if (textEl) textEl.textContent = message;
      celebration.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => celebration.classList.add('is-visible')));
    }

    if (root.dataset.confetti !== 'false') {
      launchConfetti(bar);
    }
  }

  function launchConfetti(progress) {
    const colors = ['#e8590c', '#ffd43b', '#1aa36b', '#1a1a1a', '#e64980'];
    const container = document.createElement('div');
    container.className = 'cart-progress__confetti';
    for (let i = 0; i < 30; i++) {
      const piece = document.createElement('span');
      piece.className = 'cart-progress__confetti-piece';
      piece.style.setProperty('--x', `${Math.random() * 100}%`);
      piece.style.setProperty('--delay', `${Math.random() * 200}ms`);
      piece.style.setProperty('--duration', `${900 + Math.random() * 600}ms`);
      piece.style.setProperty('--confetti-color', colors[i % colors.length]);
      container.appendChild(piece);
    }
    progress.appendChild(container);
    setTimeout(() => container.remove(), 1800);
  }

  // ---- Free gift auto-add / auto-remove ---------------------------------

  // Reconciles the cart against the configured gift threshold on every
  // drawer update. Deliberately idempotent and state-driven (re-checks
  // actual cart contents via /cart.js every time) rather than tracking
  // "did we already add it" locally -- that would drift the moment a
  // customer removes the gift line by hand, or edits the cart from another
  // tab. Fixed point: eligible+present or ineligible+absent, either of
  // which is a no-op, so this can never loop forever.
  let giftCheckInFlight = false;

  function initGiftAutoAdd() {
    if (giftCheckInFlight) return;

    const root = document.querySelector('[data-cart-drawer-root]');
    if (!root || root.dataset.giftEnabled !== 'true') return;

    const giftVariantId = root.dataset.giftVariantId;
    const threshold = parseInt(root.dataset.giftThresholdCents, 10);
    if (!giftVariantId || !threshold) return;

    giftCheckInFlight = true;
    // cache: 'no-store' is deliberate -- without it the browser can serve a
    // cached pre-removal /cart.js response, so reconcileGift keeps seeing the
    // cart as still-eligible and never removes the gift after a removal drops
    // the subtotal back below threshold (confirmed live: the gift correctly
    // auto-adds on crossing up, since a slightly stale response there still
    // reads as eligible, but never auto-removes on crossing back down).
    fetch(`${window.routes.cart_url}.js`, { cache: 'no-store' })
      .then((response) => response.json())
      .then((cart) => reconcileGift(cart, giftVariantId, threshold))
      .catch(() => {
        /* transient network failure -- next cart update will retry */
      })
      .finally(() => {
        giftCheckInFlight = false;
      });
  }

  // Returns the mutation promise (or undefined when the cart is already at
  // the fixed point) -- initGiftAutoAdd chains on it, so giftCheckInFlight
  // stays true until the mutation AND its drawer refresh complete. That
  // window is exactly when a second overlapping check used to slip in,
  // re-read the cart before the add landed, and add the gift a second time
  // (the orders with a quantity-2 gift line).
  function reconcileGift(cart, giftVariantId, threshold) {
    // ALL lines of the gift variant, not just the first: the same variant
    // can legitimately appear as two separate lines when one copy was added
    // without the _cart_reward_gift property (e.g. via a non-drawer path),
    // and a find() would leave every line after the first invisible here
    // (the orders with two separate gift lines).
    const giftLines = cart.items.filter((it) => String(it.variant_id) === String(giftVariantId));

    // Subtotal EXCLUDING the gift's own lines -- it's a Rs.0 product so this
    // never actually changes the number, but excluding it keeps the intent
    // ("spend this much on real products") correct even if the gift product
    // is ever repriced.
    const nonGiftSubtotal = cart.items
      .filter((it) => String(it.variant_id) !== String(giftVariantId))
      .reduce((sum, it) => sum + it.line_price, 0);

    const eligible = nonGiftSubtotal >= threshold;

    if (eligible && giftLines.length === 0) {
      return addGiftToCart(giftVariantId);
    }

    // Everything below normalizes existing gift lines in ONE /cart/update.js
    // call: total gift quantity must be exactly 1 when eligible, 0 when not.
    // Extra lines and quantity > 1 (no stepper in the drawer, but the cart
    // page / another tab / a race can still produce them) both clamp here.
    const targetFirstLineQty = eligible ? 1 : 0;
    const updates = {};
    giftLines.forEach((line, index) => {
      const target = index === 0 ? targetFirstLineQty : 0;
      if (line.quantity !== target) updates[line.key] = target;
    });

    if (Object.keys(updates).length > 0) {
      return setGiftLineQuantities(updates);
    }
  }

  function addGiftToCart(variantId) {
    return fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _cart_reward_gift: 'true' } }],
      }),
    })
      .then(refreshCartDrawerSection)
      .catch(() => {});
  }

  // /cart/update.js instead of /cart/change.js: it accepts every line-key ->
  // quantity pair in a single request, so de-duplicating two gift lines and
  // clamping quantity can't be split into racing calls.
  function setGiftLineQuantities(updates) {
    return fetch(`${window.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    })
      .then(refreshCartDrawerSection)
      .catch(() => {});
  }

  // Same section-refresh pattern as quickAddFromUpsell below -- fetch the
  // re-rendered drawer HTML and swap it in, then re-run init() so every
  // other delight (progress bar sweep, badges, etc.) picks up the new state.
  function refreshCartDrawerSection() {
    // Same no-store rationale as initGiftAutoAdd's /cart.js fetch above --
    // this section-rendering GET can otherwise be served stale too.
    return fetch(`${window.routes.cart_url}?section_id=cart-drawer`, { cache: 'no-store' })
      .then((r) => r.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const fresh = html.querySelector('#CartDrawer');
        const current = document.getElementById('CartDrawer');
        captureFillPercent();
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        init();
      });
  }

  // ---- Countdown ----------------------------------------------------------

  function initCountdown(drawer) {
    if (countdownInterval) clearInterval(countdownInterval);

    const el = drawer.querySelector('[data-cart-countdown]');
    if (!el) return;
    const clock = el.querySelector('[data-cart-countdown-clock]');
    const fill = el.querySelector('[data-cart-countdown-fill]');
    const minutes = parseInt(el.dataset.countdownMinutes, 10) || 10;

    // sessionStorage, not localStorage: "according to each session" means a
    // new tab/browser restart should get its own fresh 10 minutes, not
    // resume a countdown a returning visitor left running days ago.
    let startedAt = parseInt(sessionStorage.getItem(STORAGE_COUNTDOWN_START), 10);
    if (!startedAt) {
      startedAt = Date.now();
      try {
        sessionStorage.setItem(STORAGE_COUNTDOWN_START, String(startedAt));
      } catch (e) {
        /* storage unavailable -- countdown still works, just restarts on next init() */
      }
    }

    const totalMs = minutes * 60000;

    const tick = () => {
      // Tab inactive: rAF/timers throttle automatically in background tabs,
      // and since we compute from a stored wall-clock start time (not a
      // decrementing counter), the displayed value is always correct
      // whenever the tab becomes active again -- nothing to resume.
      const remaining = totalMs - (Date.now() - startedAt);
      if (remaining <= 0) {
        el.classList.add('is-expired');
        el.innerHTML = '<span class="cart-countdown__expired">Your cart reservation has expired</span>';
        clearInterval(countdownInterval);
        return;
      }
      const m = Math.floor(remaining / 60000);
      const s = Math.floor((remaining % 60000) / 1000);
      clock.textContent = `${m}:${String(s).padStart(2, '0')}`;
      if (fill) fill.style.width = `${Math.max(0, (remaining / totalMs) * 100)}%`;
    };

    tick();
    countdownInterval = setInterval(tick, 1000);
  }

  // ---- Upsell / "Frequently Bought Together" ---------------------------

  // snippets/cart-upsell.liquid renders the actual recommended products
  // server-side (per-cart-item, same-collection) -- this just reveals the
  // section and wires up its "Add" buttons. No fetching happens here.
  function initUpsell(drawer) {
    const container = drawer.querySelector('[data-cart-upsell]');
    if (!container) return;

    const list = container.querySelector('[data-cart-upsell-list]');
    const items = list ? list.querySelectorAll('.cart-upsell__item') : [];
    if (!items.length) return;

    container.hidden = false;
    items.forEach((item, i) => {
      requestAnimationFrame(() => setTimeout(() => item.classList.add('is-visible'), i * 80));
    });

    list.querySelectorAll('.cart-upsell__add').forEach((button) => {
      button.addEventListener('click', () => quickAddFromUpsell(button));
    });
  }

  // Nudges the cart's own scroll position down after a genuine add, just
  // enough to hint that there's more below (Frequently Bought Together) --
  // deliberately a partial scrollBy, not scrollIntoView, so the drawer
  // doesn't jump far enough to lose the item that was just added out of view.
  function initScrollToUpsellHint(drawer, wasGenuineAdd) {
    if (!wasGenuineAdd) return;

    const upsell = drawer.querySelector('[data-cart-upsell]');
    const scrollContainer = drawer.querySelector('.cart-drawer__scroll');
    if (!upsell || upsell.hidden || !scrollContainer) return;

    // Delayed so it reads as "the cart nudged itself" after the success
    // banner/animations settle, not an abrupt jump fighting the open transition.
    setTimeout(() => {
      scrollContainer.scrollBy({ top: 220, behavior: reducedMotion ? 'auto' : 'smooth' });
    }, 450);
  }

  function quickAddFromUpsell(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = '...';

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then((res) => res.json())
      .then((response) => {
        button.textContent = '✓';
        button.classList.add('is-added');

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: response.items ? response.items[0] : response,
          });
        }

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) refreshCartDrawerSection();
      })
      .catch(() => {
        button.disabled = false;
        button.textContent = originalText;
      });
  }

  // ---- Shake when the cart becomes empty ------------------------------------

  function initEmptyShake(drawer) {
    const empty = drawer.querySelector('[data-cart-drawer-empty]');
    if (!empty) return;
    empty.classList.add('is-shaking');
    setTimeout(() => empty.classList.remove('is-shaking'), 600);
  }

  // ---- Cart icon bubble pulse ------------------------------------------------

  function initCartBubblePulse() {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return;
    bubble.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.35)' },
        { transform: 'scale(1)' },
      ],
      { duration: 350, easing: 'ease-out' }
    );
  }

  // Re-run for every drawer content replacement, regardless of which code
  // path caused it (add via renderContents(), quantity change via
  // updateQuantity(), or another tab syncing via onCartUpdate()) -- a
  // MutationObserver is the one hook common to all three. init() itself
  // pauses this observer while it makes its own DOM writes (see above).
  if (drawerRoot) {
    let debounceTimer;
    observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(init, 50);
    });
    resumeObserver();
  }

  // Sweep-on-open: reopening an unchanged drawer does no childList mutation
  // at all, so without this the bar would only ever animate on cart changes.
  // Watches <cart-drawer>'s class for CartDrawer.open() adding 'active' --
  // it never writes back to that element, so it can't loop with the
  // childList observer. When an add triggered this open, a captured
  // pre-replacement value is pending and init()'s continuity sweep is about
  // to run -- defer to it rather than double-animating.
  const cartDrawerRootEl = document.querySelector('[data-cart-drawer-root]');
  if (cartDrawerRootEl) {
    let wasActive = cartDrawerRootEl.classList.contains('active');
    new MutationObserver(() => {
      const isActive = cartDrawerRootEl.classList.contains('active');
      if (isActive && !wasActive && pendingPreviousPercents === null) {
        document.querySelectorAll('[data-cart-progress-fill]').forEach((fill) => {
          sweepFill(fill, 0, parseFloat(fill.dataset.targetPercent || '0'));
        });
      }
      wasActive = isActive;
    }).observe(cartDrawerRootEl, { attributes: true, attributeFilter: ['class'] });
  }

  document.addEventListener('DOMContentLoaded', init);
})();
